/**
 * A small, hand-drawn inline-SVG icon set for the activity feed
 * (DIRECTION.md §1.3) — ten glyphs, one per `ActivityIconKind`
 * (`src/log/logFormat.ts`). No emoji, no icon font, no network fetch: each
 * icon is a tiny `<svg>` built at render time from a fixed path/shape
 * table, coloured via `currentColor` so it inherits the row's category
 * colour automatically.
 */

import type { ActivityIconKind } from "./logFormat";

const NS = "http://www.w3.org/2000/svg";

function svg(children: SVGElement[]): SVGSVGElement {
  const el = document.createElementNS(NS, "svg");
  el.setAttribute("viewBox", "0 0 16 16");
  el.setAttribute("fill", "none");
  el.setAttribute("stroke", "currentColor");
  el.setAttribute("stroke-width", "1.4");
  el.setAttribute("stroke-linecap", "round");
  el.setAttribute("stroke-linejoin", "round");
  for (const child of children) el.appendChild(child);
  return el;
}

function path(d: string): SVGPathElement {
  const el = document.createElementNS(NS, "path");
  el.setAttribute("d", d);
  return el;
}

function circle(cx: number, cy: number, r: number): SVGCircleElement {
  const el = document.createElementNS(NS, "circle");
  el.setAttribute("cx", String(cx));
  el.setAttribute("cy", String(cy));
  el.setAttribute("r", String(r));
  return el;
}

/** One shape-builder per icon kind — a plain lookup table, no per-kind
 * branch logic elsewhere in the app needs to know these shapes. */
const BUILDERS: Readonly<Record<ActivityIconKind, () => SVGSVGElement>> = {
  // A small burst — a wake/decision moment.
  wake: () =>
    svg([
      path("M8 1v3M8 12v3M1 8h3M12 8h3M3 3l2 2M11 11l2 2M13 3l-2 2M5 11l-2 2"),
      circle(8, 8, 2),
    ]),
  // An order ticket outline with a corner fold.
  order: () => svg([path("M4 2h6l2 2v10H4z"), path("M10 2v2h2"), path("M6 8h4M6 11h4")]),
  // A check inside a circle — a completed fill.
  fill: () => svg([circle(8, 8, 6.2), path("M5.3 8.2l1.8 1.8 3.6-3.8")]),
  // Two arrows reconciling toward each other.
  reconcile: () => svg([path("M2 6h9l-2.2-2.2M14 10H5l2.2 2.2")]),
  // A small spark/chat glyph — a semantic (LLM) call.
  semantic: () => svg([path("M2 3h12v7H6l-3 3v-3H2z"), path("M5 6.5h6")]),
  // A pulse/heartbeat line — life expectancy.
  life: () => svg([path("M1 8h3l1.5-4 2 8 1.5-5 1 1h4")]),
  // Two overlapping nodes — family/lineage.
  family: () => svg([circle(6, 6, 2.6), circle(11, 10, 2.6)]),
  // A small candle/bar — market data.
  market: () => svg([path("M4 2v12M12 6v8"), path("M2.5 5h3M10.5 8h3")]),
  // A steady square wave — runtime/infra heartbeat-like bookkeeping.
  runtime: () => svg([path("M1 8h3l1.5-3 2 6 1.5-3h6")]),
  // A triangle with an exclamation — something worth a second look.
  alert: () => svg([path("M8 2l6.5 11.5h-13z"), path("M8 6.5v3.2"), circle(8, 11.6, 0.2)]),
};

export function renderActivityIcon(kind: ActivityIconKind): SVGSVGElement {
  const build = BUILDERS[kind] ?? BUILDERS.runtime;
  return build();
}
